import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Download, Info, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { STAGE_LABELS, canExportMarketing, defaultPeriod, formatDays, formatMoneyMap, formatRate, marketingError, periodProblem, touchKindLabel } from "../lib/marketing";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

function Figure({ label, value, method, testid }) {
  return (
    <Card className="p-4 space-y-1" data-testid={testid}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-display font-bold text-slate-900 tabular-nums">{value}</div>
      <details className="text-xs text-slate-500">
        <summary className="cursor-pointer inline-flex items-center gap-1 select-none"><Info size={12} aria-hidden="true" />Méthode</summary>
        <p className="mt-1 leading-relaxed" data-testid={`${testid}-method`}>{method}</p>
      </details>
    </Card>
  );
}

function ExportButtons({ period }) {
  const [busy, setBusy] = useState("");
  const download = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.get(`/marketing/export/${kind}.csv`, { params: period, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `jamora-${kind === "campaigns" ? "campagnes" : "partenaires"}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Vous n'avez pas accès à cet export." : "Impossible de télécharger l'export.");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {[["campaigns", "Exporter les campagnes"], ["partners", "Exporter les partenaires"]].map(([kind, label]) => (
        <Button key={kind} type="button" variant="outline" size="sm" onClick={() => download(kind)} disabled={!!busy} data-testid={`export-${kind}`}>
          {busy === kind ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}{label}
        </Button>
      ))}
    </div>
  );
}

export default function MarketingIndicators() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(() => defaultPeriod());
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const seq = useRef(0);
  const problem = periodProblem(period);
  const load = useCallback(async () => {
    if (periodProblem(period)) return;
    const mine = ++seq.current;
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { data: body } = await api.get("/marketing/indicators", { params: period });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(marketingError(e, "Impossible de charger les indicateurs."));
      setPhase("error");
    }
  }, [period]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const costs = data?.costs;
  const showMoney = !!costs?.allowed && costs.finance_active;
  const totals = data?.totals;
  return (
    <div className="space-y-4" data-testid="marketing-indicators-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Indicateurs</h1>
          <p className="text-sm text-slate-500">Combien de pharmacies chaque origine a amenées, combien sont devenues clientes, et à quel coût. Chaque chiffre dit comment il est calculé.</p>
        </div>
        {canExportMarketing(user) && !problem && <ExportButtons period={period} />}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label htmlFor="ind-from" className="text-xs">Du</Label>
          <Input id="ind-from" type="date" value={period.from} onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))} className="h-9 w-40" data-testid="ind-from" /></div>
        <div className="space-y-1"><Label htmlFor="ind-to" className="text-xs">Au</Label>
          <Input id="ind-to" type="date" value={period.to} onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))} className="h-9 w-40" data-testid="ind-to" /></div>
        {problem && <p className="text-sm text-red-600 pb-2" role="alert" data-testid="ind-problem">{problem}</p>}
      </div>

      {phase === "loading" && <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data && (
        <>
          {totals.leads === 0 ? (
            <Card className="p-8 text-center space-y-2" data-testid="ind-empty">
              <h3 className="font-medium text-slate-900">Aucune origine enregistrée sur cette période</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">{data.limits} Ajoutez l'origine d'une pharmacie depuis sa fiche, ou choisissez une campagne en la créant.</p>
            </Card>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Figure label="Leads" value={totals.leads} method={data.methods.leads} testid="fig-leads" />
                <Figure label="Taux de conversion" value={formatRate(totals.conversion_rate)} method={data.methods.conversion} testid="fig-conversion" />
                <Figure label="Délai de conversion (médiane)" value={formatDays(totals.median_conversion_days)} method={data.methods.delay} testid="fig-delay" />
                {costs.revenue_allowed
                  ? <Figure label="Revenu mensuel attribué" value={formatMoneyMap(totals.revenue) || "—"} method={data.methods.revenue} testid="fig-revenue" />
                  : <Figure label="Clients obtenus" value={totals.converted} method={data.methods.conversion} testid="fig-clients" />}
              </div>

              <Card className="p-4" data-testid="ind-stages">
                <h3 className="font-display font-semibold text-slate-900 mb-2">Leads par étape actuelle</h3>
                <ul className="flex flex-wrap gap-2">
                  {Object.entries(totals.by_stage).map(([stage, n]) => (
                    <li key={stage} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm text-slate-700">{STAGE_LABELS[stage] || stage} <span className="font-medium tabular-nums">{n}</span></li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-slate-500">{data.methods.stages}</p>
              </Card>

              <Card className="hidden md:block overflow-hidden" data-testid="ind-table">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Origine</TableHead><TableHead className="text-right">Leads</TableHead><TableHead className="text-right">Convertis</TableHead>
                    <TableHead className="text-right">Conversion</TableHead><TableHead className="text-right">Délai</TableHead>
                    {showMoney && <><TableHead>Dépenses reliées</TableHead><TableHead>Coût par lead</TableHead><TableHead>Coût d'acquisition</TableHead></>}
                    {costs.revenue_allowed && <TableHead>Revenu mensuel</TableHead>}
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.rows.map((r) => (
                      <TableRow key={`${r.kind}-${r.id || r.name}`} data-testid={`ind-row-${r.number || r.name}`}>
                        <TableCell><div className="font-medium text-slate-900">{r.name}</div><div className="text-xs text-slate-500">{touchKindLabel(r.kind)}{r.number ? ` · ${r.number}` : ""}</div></TableCell>
                        <TableCell className="text-right tabular-nums">{r.leads}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.converted}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatRate(r.conversion_rate)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatDays(r.median_conversion_days)}</TableCell>
                        {showMoney && <>
                          <TableCell className="text-sm">{r.kind === "campaign" ? (formatMoneyMap(r.spend) || <span className="text-slate-400">—</span>) : <span className="text-slate-400">—</span>}</TableCell>
                          <TableCell className="text-sm">{r.kind === "campaign" ? (formatMoneyMap(r.cost_per_lead) || <span className="text-slate-400">—</span>) : <span className="text-slate-400">—</span>}</TableCell>
                          <TableCell className="text-sm">{r.kind === "campaign" ? (formatMoneyMap(r.cac) || <span className="text-slate-400">—</span>) : <span className="text-slate-400">—</span>}</TableCell>
                        </>}
                        {costs.revenue_allowed && <TableCell className="text-sm">{formatMoneyMap(r.revenue) || <span className="text-slate-400">—</span>}</TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
              <Card className="md:hidden divide-y divide-slate-100" data-testid="ind-cards">
                {data.rows.map((r) => (
                  <div key={`${r.kind}-${r.id || r.name}`} className="px-4 py-3 space-y-1">
                    <div className="font-medium text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-500">{touchKindLabel(r.kind)}{r.number ? ` · ${r.number}` : ""}</div>
                    <div className="text-sm text-slate-700 tabular-nums">{r.leads} lead{r.leads > 1 ? "s" : ""} · {r.converted} converti{r.converted > 1 ? "s" : ""} · {formatRate(r.conversion_rate)}</div>
                    {showMoney && r.kind === "campaign" && <div className="text-xs text-slate-600">Coût par lead : {formatMoneyMap(r.cost_per_lead) || "—"} · Coût d'acquisition : {formatMoneyMap(r.cac) || "—"}</div>}
                  </div>
                ))}
              </Card>

              {showMoney && (
                <Card className="p-4 space-y-1 text-xs text-slate-500" data-testid="ind-money-methods">
                  <p><span className="font-medium text-slate-700">Coût par lead.</span> {data.methods.cost_per_lead}</p>
                  <p><span className="font-medium text-slate-700">Coût d'acquisition.</span> {data.methods.cac}</p>
                </Card>
              )}
              {costs.allowed && !costs.finance_active && <p className="text-sm text-slate-500" data-testid="ind-finance-off">Les finances internes ne sont pas activées : les coûts ne peuvent pas être calculés.</p>}
              <p className="text-xs text-slate-500">{data.limits}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
